// App-wide auth state. Hydrates from /api/auth/me on boot, then holds the user.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { api } from "../lib/api";
import type { User } from "../lib/types";

type Status = "loading" | "ready";

interface AuthValue {
  status: Status;
  user: User | null;
  /** Attempt login; on success returns the user (with isFirstLogin) and stores it. */
  login: (handle: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  /** Replace the current user (e.g. after a profile emoji/avatar change). */
  updateUser: (user: User) => void;
  /** Admin-only: when true, Lionel is previewing the site as a friend sees it. */
  previewPublic: boolean;
  setPreviewPublic: (v: boolean) => void;
  /** True when admin AND not previewing public — gates every admin affordance. */
  isAdminView: boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [user, setUser] = useState<User | null>(null);
  const [previewPublic, setPreviewPublic] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .me()
      .then(({ user }) => alive && setUser(user))
      .catch(() => alive && setUser(null))
      .finally(() => alive && setStatus("ready"));
    return () => {
      alive = false;
    };
  }, []);

  const login = useCallback(async (handle: string, password: string) => {
    const { user } = await api.login(handle, password);
    setUser(user);
    setPreviewPublic(false);
    return user;
  }, []);

  const logout = useCallback(async () => {
    await api.logout().catch(() => {});
    setUser(null);
    setPreviewPublic(false);
  }, []);

  const updateUser = useCallback((next: User) => setUser(next), []);

  const isAdminView = user?.role === "admin" && !previewPublic;

  const value = useMemo<AuthValue>(
    () => ({ status, user, login, logout, updateUser, previewPublic, setPreviewPublic, isAdminView }),
    [status, user, login, logout, updateUser, previewPublic, isAdminView],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
